import {useEffect, useRef, useState} from 'react';
import {useLocation, useMatches} from 'react-router-dom';
import {APIClient} from '../utils/APIClient';
import {LocalStorage} from '../utils/LocalStorage';
import {API_URL} from '../settings/local_settings';
import {CLARITY_APP_PROJECT_ID} from '../settings/settings';
import {clarityAudience, isSignupPath} from '../utils/clarity/clarityAudience';
import {ClarityRecorder} from '../utils/clarity/ClarityRecorder';
import {isEmbedded} from '../utils/floatingWidgets';

// Grabaciones de Clarity en la app (UD-7 de /ux, 18/10/2026): el alta y las agencias en prueba o
// vencidas sin pagar. Decide en cada cambio de ruta; no renderiza nada.
//
// Con sesión, la decisión sale de `get-logged-user-data/` para ESE token (con `cache` comparte el
// pedido que ya hacen el marco y el refresco de estado), que se vuelve a pedir en cada navegación:
// una cuenta recién creada se confirma sin agencia y recién después pasa a «Free Trial». Mientras no
// hay respuesta para el token actual no arranca, y si ya grababa sólo sigue en el alta (una cuenta
// nueva va a «Crea tu agencia»; una existente que entró por el `/signup` va a `/` y se detiene).
// Una vez detenido no vuelve a arrancar hasta la próxima carga.

const PENDING = 'pending';
const TOKEN_KEY = 'logged_user_token';

// La pantalla, igual que `RouteTracker` (`handle.screen` del último match). `/signup` no tiene
// `handle` porque no cuenta para el seguimiento de pantallas, pero acá es el primer paso del alta.
export function screenOf(matches, pathname) {
    const last = matches && matches.length ? matches[matches.length - 1] : null;
    const screen = last && last.handle && last.handle.screen;
    if (screen) return screen;
    return pathname === '/signup' ? 'signup' : null;
}

function sessionFor(token, confirmed) {
    if (!token) return null;
    const userType = LocalStorage.fetchUserType();
    if (userType !== 'AGENCY_USER') return {userType};
    if (!confirmed || confirmed.token !== token) return PENDING;
    const {data} = confirmed;
    return {
        userType,
        isSuperAdmin: data.is_super_admin,
        agencyStatus: data.agency_status,
        userId: data.id,
        agencyId: data.agency_id,
        daysRemaining: data.free_trial_days_remaining,
    };
}

// Lo que decide y lo que se manda. Si nada de esto cambió, la respuesta nueva no re-renderiza.
function sameDecision(a, b) {
    return !!a && !!b && a.token === b.token
        && ['is_super_admin', 'agency_status', 'id', 'agency_id', 'free_trial_days_remaining']
            .every((key) => a.data[key] === b.data[key]);
}

export function ClarityTracker() {
    const {pathname, search, hash} = useLocation();
    const matches = useMatches();
    const token = LocalStorage.fetchUserToken();
    const [confirmed, setConfirmed] = useState(null);
    // El token con el que se identificó la grabación: si cambia, es otra persona.
    const identifiedToken = useRef(null);
    const screen = screenOf(matches, pathname);

    useEffect(() => {
        if (!CLARITY_APP_PROJECT_ID || !token || LocalStorage.fetchUserType() !== 'AGENCY_USER') return;
        let cancelled = false;
        new APIClient(API_URL + 'get-logged-user-data/').call('GET', {}, {cache: true}).then((data) => {
            // Una respuesta de otra sesión (se cerró o se cambió de cuenta en el medio) no decide nada.
            if (cancelled || !data || data.status !== 'OK' || LocalStorage.fetchUserToken() !== token) return;
            const next = {token, data};
            setConfirmed((current) => (sameDecision(current, next) ? current : next));
        }).catch(() => {
            // Sin respuesta no arranca; la próxima navegación lo vuelve a pedir.
        });
        return () => {
            cancelled = true;
        };
    }, [token, pathname]);

    // Cerrar sesión o entrar con otra cuenta en OTRA pestaña no navega en ésta: se detiene igual.
    useEffect(() => {
        const onStorage = (event) => {
            if (event.key === TOKEN_KEY || event.key === null) ClarityRecorder.stop();
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    useEffect(() => {
        if (!CLARITY_APP_PROJECT_ID) return;
        // La vista previa del portal es la app adentro de un iframe del mismo origen: el tag de la
        // página de afuera ya la ve. Un segundo tag duplicaría las páginas.
        if (isEmbedded()) return;

        if (identifiedToken.current && identifiedToken.current !== token) {
            ClarityRecorder.stop();
            return;
        }
        const session = sessionFor(token, confirmed);
        if (session === PENDING) {
            if (!isSignupPath(pathname)) ClarityRecorder.stop();
            return;
        }

        // La URL que ve el tag es la del navegador: `/signup` saca el `token` con `replaceState`,
        // por fuera del router.
        const estado = clarityAudience({
            pathname: window.location.pathname,
            search: window.location.search,
            hash: window.location.hash,
            session,
        });
        if (!estado) {
            ClarityRecorder.stop();
            return;
        }
        if (!ClarityRecorder.start(CLARITY_APP_PROJECT_ID)) return;

        // Clarity pide el `identify` en cada página; las etiquetas van con él para que toda página
        // grabada se pueda filtrar.
        if (session) {
            identifiedToken.current = token;
            ClarityRecorder.identify(session.userId, session.agencyId);
            ClarityRecorder.tag('agency_id', session.agencyId);
            ClarityRecorder.tag('dias_restantes', session.daysRemaining);
        }
        ClarityRecorder.tag('estado', estado);
        ClarityRecorder.tag('pantalla', screen);
    }, [pathname, search, hash, screen, token, confirmed]);

    return null;
}
